import { useEffect, useState } from 'react';
import { progressionService, type Achievement } from '@/services/ProgressionService';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AchievementsPanel({ open, onClose }: Props) {
  const [achievements, setAchievements] = useState<Achievement[]>([]);

  useEffect(() => {
    if (!open) return;
    setAchievements(progressionService.getAllAchievements());
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-auto animate-fade-in">
      <div className="w-[400px] max-h-[75vh] overflow-auto rounded-xl bg-game-panel/95 border-2 border-game-panel-border text-white p-6 space-y-4 shadow-2xl animate-scale-in">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚖️</span>
            <h2 className="text-xl font-bold text-primary">Achievements</h2>
          </div>
          <button
            className="text-sm px-3 py-1.5 rounded-lg bg-secondary/60 hover:bg-secondary border border-border/40 hover:border-primary/50 transition-all"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        
        <div className="space-y-3 pr-2">
          {achievements.map((ach) => (
            <div
              key={ach.id}
              className={cn(
                "rounded-xl border-2 px-4 py-3 transition-all",
                ach.unlocked 
                  ? "border-game-correct/60 bg-game-correct/15 shadow-lg shadow-game-correct/10" 
                  : "border-border/60 bg-secondary/40 hover:bg-secondary/60"
              )}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{ach.unlocked ? '✓' : '○'}</span>
                  <span className="font-semibold text-foreground">{ach.name}</span>
                </div>
                <span className={cn(
                  "text-xs px-2 py-1 rounded-md font-medium whitespace-nowrap",
                  ach.unlocked 
                    ? "bg-game-correct/30 text-game-correct" 
                    : "bg-secondary/60 text-muted-foreground"
                )}>
                  {ach.unlocked ? 'Unlocked' : `${ach.progress ?? 0}/${ach.requirement.value}`}
                </span>
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed pl-7">
                {ach.description}
              </div>
              {!ach.unlocked && ach.progress !== undefined && ach.progress > 0 && (
                <div className="mt-2 pl-7">
                  <div className="h-1.5 bg-secondary/60 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-accent to-primary rounded-full transition-all duration-500"
                      style={{ width: `${(ach.progress / ach.requirement.value) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
          {achievements.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              <div className="text-4xl mb-3 opacity-30">⚖️</div>
              No achievements yet. Start playing to unlock them!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
